import React, { useState, useEffect } from 'react'
import styles from './SelectedFile.module.css'
import { FILE_TYPE_PHP, FILE_TYPE_HTML, FILE_TYPE_PDF } from '../../constants'
import createFilePicker from '../../../../utils/filePicker'
import { createFileInput, isTotalSizeWithinLimit, isWasmerFile, isValidFileSize, isFileNameUnique } from 'utils/general'
import { PRO_MAX_FILE_SIZES, PLANS } from 'constants/plans'
import {
  DROPZONE_ACCEPTED_FILES,
  DROPZONE_ACCEPTED_FILES_PRO,
  MIXED_FOLDER_ERROR_MSG,
  MIXED_ZIP_ERROR_MSG
} from '../../../../constants'
import FileListModal from 'components/FileListModal/FileListModal'
import FileListView from '../FileListView/FileListView'
import useCheckScreenSize from 'hooks/checkMobileScreen'
import {
  findHtmlFilesInZipFile,
  isIndexFile,
  getPhpFiles,
  getPythonFiles,
  getNodeFiles,
  getFileType,
  getFileContentType,
  isLottieFile,
  determineHtmlIndexFile,
  isHtmlFileExisting
} from 'helpers/file-type'
import { TLDS } from 'constants'
import { MAX_FILE_SIZE_FREE } from '../../../../constants'
import { isZipFile } from 'components/CreateUpdateSiteModal/interface'
import toast from 'react-hot-toast'

const FILE_ICONS = {
  [FILE_TYPE_HTML]: 'html-file.png',
  [FILE_TYPE_PDF]: 'pdf-file.png',
  [FILE_TYPE_PHP]: 'php-file.png',
  'application/zip': 'zip-folder.png',
  'application/x-zip-compressed': 'zip-folder.png',
  'application/octet-stream': 'zip-folder.png'
}

export const getFileIcon = (fileType, fileName = '') => {
  if (isLottieFile(fileName)) {
    return 'file-x.svg'
  }
  return FILE_ICONS[fileType] || 'file-x.svg'
}

export const shouldProcessAsZip = (fileType, fileName = '') => {
  return isZipFile(fileType) && !isLottieFile(fileName)
}

const SelectedFile = ({
  draggedFile,
  productId,
  setFieldValue,
  className = '',
  maxFileSizeMB = 3,
  domainValues,
  uploadValues,
  showUpgradeModal,
  isGithub,
  site,
  resetDraggedFile
}) => {
  const { selectedFile, addedFiles = [], indexFile, fileUploadLimit } = uploadValues
  const [showFileList, setShowFileList] = useState(false)
  const [htmlFiles, setHtmlFiles] = useState([])
  const [phpFiles, setPhpFiles] = useState([])
  const [acceptedFiles, setAcceptedFiles] = useState(
    getFileContentType(selectedFile?.name) || selectedFile?.fileType || selectedFile?.type
  )
  const { isMobile } = useCheckScreenSize()
  const allFiles = selectedFile ? [selectedFile, ...addedFiles] : []
  const hasSingleFile = allFiles.length === 1

  const zipFileUploaded = async (file) => {
    try {
      const filesFound = await findHtmlFilesInZipFile(file)
      const { containsPhpFile, phpFiles } = await getPhpFiles(file)
      const { containsPythonFile, pythonFiles } = await getPythonFiles(file)
      const { containsNodeFile, isReact } = await getNodeFiles(file)

      if (containsNodeFile || isReact) {
        file.isNodeOrReactZip = true
        if (domainValues) {
          setFieldValue('domainValues.domainSuffix', TLDS.TIINY_IO)
          setFieldValue('domainValues.tlds', [TLDS.TIINY_IO])
        }
        setFieldValue('uploadValues.indexFile', null)
        setFieldValue('uploadValues.isZipPhpFile', false)
        setPhpFiles([])
        setHtmlFiles([])
        setShowFileList(false)
      } else if ((containsPhpFile && phpFiles?.length > 0) || (containsPythonFile && pythonFiles?.length > 0)) {
        // PHP or Python files exist - always show modal to let user choose
        setPhpFiles(containsPhpFile ? phpFiles : pythonFiles)
        setHtmlFiles([])
        setShowFileList(true)
        // Don't set index - let user choose from modal
      } else {
        // Only HTML files - use smart logic
        const { htmlFilesFound, shouldShowModal, indexFileToSet } = determineHtmlIndexFile(
          filesFound.map((f) => ({ name: f, path: f }))
        )

        setHtmlFiles(htmlFilesFound)
        setShowFileList(shouldShowModal)

        // Auto-set index if determined (single file or index.html exists)
        if (indexFileToSet) {
          setFieldValue('uploadValues.indexFile', indexFileToSet)
        }
      }
    } catch (error) {
      console.error('Error processing ZIP file:', error)
    }
  }

  const isNewHtmlFilesUploaded = (files) => {
    // Only process if there are HTML files
    if (!isHtmlFileExisting(files)) {
      return
    }

    const { htmlFilesFound, shouldShowModal, indexFileToSet } = determineHtmlIndexFile(files)

    // Always update HTML files list
    setHtmlFiles(htmlFilesFound)

    // Show modal if needed
    if (shouldShowModal) {
      setShowFileList(true)
    }

    // Set index file if determined
    if (indexFileToSet) {
      setFieldValue('uploadValues.indexFile', indexFileToSet)
    } else if (htmlFilesFound.length === 0) {
      // Clear if no HTML files
      setFieldValue('uploadValues.indexFile', null)
    }
  }

  const handleFileDelete = (file, index) => {
    if (index === 0) {
      if (addedFiles && addedFiles.length > 0) {
        const newMainFile = addedFiles[0]
        const remainingFiles = addedFiles.slice(1)

        setFieldValue('uploadValues', {
          ...uploadValues,
          selectedFile: newMainFile,
          fileType: getFileType(newMainFile.name),
          addedFiles: remainingFiles
        })

        if (shouldProcessAsZip(newMainFile.type, newMainFile.name)) {
          zipFileUploaded(newMainFile)
        }
      } else {
        setFieldValue('uploadValues', {
          ...uploadValues,
          selectedFile: null,
          fileType: null,
          indexFile: null,
          isZipPhpFile: false
        })

        setHtmlFiles([])
        setPhpFiles([])
      }
    } else {
      const updatedAdditionalFiles = [...addedFiles]
      updatedAdditionalFiles.splice(index - 1, 1)
      setFieldValue('uploadValues.addedFiles', updatedAdditionalFiles)

      isNewHtmlFilesUploaded([selectedFile, ...updatedAdditionalFiles])
    }
  }

  const handleFilesSelected = async (files, isReplacement = false) => {
    if (!files || !files.length) return

    const isTrial = !productId
    const maxSizeMB = isTrial ? MAX_FILE_SIZE_FREE : fileUploadLimit || PRO_MAX_FILE_SIZES[productId] || maxFileSizeMB
    let additionalFiles = []

    if (!selectedFile || isReplacement) {
      const mainFile = files[0]

      for (const file of files) {
        if (!isValidFileSize(file, maxSizeMB)) {
          if (showUpgradeModal) {
            showUpgradeModal(file, maxSizeMB)
          } else {
            alert(`File "${file.name}" should be less than ${maxSizeMB}MB on your current plan.`)
          }
          return
        }
      }

      if (files.some((file) => shouldProcessAsZip(file.type, file.name)) && files.length > 1) {
        if (files.some((file) => file.isFolder)) {
          toast.error(MIXED_FOLDER_ERROR_MSG)
        } else {
          toast.error(MIXED_ZIP_ERROR_MSG)
        }
        return
      }
      const fileType = getFileType(mainFile.name)
      setAcceptedFiles(mainFile.type)

      if (shouldProcessAsZip(mainFile.type, mainFile.name)) {
        await zipFileUploaded(mainFile)
      } else if (isWasmerFile(mainFile)) {
        if (domainValues) {
          setFieldValue('domainValues', {
            ...domainValues,
            domainSuffix: TLDS.TIINY_IO,
            tlds: [TLDS.TIINY_IO]
          })
        }
      }

      if (files.length > 1) {
        additionalFiles = files.slice(1)
        const allFileNames = new Set([mainFile.name])
        let duplicateFound = false

        additionalFiles.forEach((file) => {
          if (allFileNames.has(file.name)) {
            duplicateFound = true
          }
          allFileNames.add(file.name)
        })

        if (duplicateFound) {
          alert('Please upload files with different names')
          return
        }

        const totalSize = [mainFile, ...additionalFiles].reduce((sum, file) => sum + file.size, 0) / (1024 * 1024)
        if (totalSize > maxSizeMB) {
          if (showUpgradeModal) {
            showUpgradeModal(mainFile, maxSizeMB, true)
          } else {
            alert(`Total files should be less than ${maxSizeMB}MB on your current plan.`)
          }
          return
        }
      }

      setFieldValue('uploadValues.selectedFile', mainFile)
      setFieldValue('uploadValues.addedFiles', additionalFiles)
      setFieldValue('uploadValues.fileType', fileType)
      setFieldValue('uploadValues.indexFile', null)

      isNewHtmlFilesUploaded(files.length > 1 ? files : [mainFile])

      return
    } else {
      const getFileName = (file) => {
        return (file?.name || file?.path || file?.fileName || '').toLowerCase().trim()
      }

      const existingFileNames = allFiles.map(getFileName).filter(Boolean)

      if (
        [...allFiles, ...files].some((file) => shouldProcessAsZip(file.type || file.fileType, file.name)) &&
        [...allFiles, ...files].length > 1
      ) {
        if ([...allFiles, ...files].some((file) => file.isFolder)) {
          toast.error(MIXED_FOLDER_ERROR_MSG)
        } else {
          toast.error(MIXED_ZIP_ERROR_MSG)
        }
        return
      }

      const validFiles = []
      const newFileNames = new Set()
      let filesToRemove = []

      for (const file of files) {
        const fileName = getFileName(file)

        if (!fileName) {
          alert('Invalid file name detected')
          return
        }

        if (existingFileNames.includes(fileName)) {
          const existingIndex = allFiles.findIndex((f) => getFileName(f) === fileName)
          if (existingIndex === 0) {
            await handleFilesSelected([file], true)
            return
          } else if (existingIndex > 0) {
            filesToRemove.push(existingIndex - 1)
          }
        }

        if (newFileNames.has(fileName)) {
          alert(`Duplicate file "${file.name}" in selection. Please upload files with different names.`)
          return
        }

        newFileNames.add(fileName)

        if (isTotalSizeWithinLimit(file, [...allFiles, ...validFiles], maxSizeMB)) {
          validFiles.push(file)
        } else {
          if (showUpgradeModal) {
            showUpgradeModal(file, maxSizeMB, true)
          } else {
            alert(`Total files should be less than ${maxSizeMB}MB on your current plan.`)
          }
          return
        }
      }

      if (validFiles.length > 0) {
        const updatedAddedFiles = addedFiles.filter((_, index) => !filesToRemove.includes(index))
        const finalAddedFiles = [...updatedAddedFiles, ...validFiles]
        setFieldValue('uploadValues.addedFiles', finalAddedFiles)
        isNewHtmlFilesUploaded([selectedFile, ...finalAddedFiles])
      }
    }
  }

  const handleAddMoreFiles = (e) => {
    if (e) e.preventDefault()

    const isTrial = !productId
    const maxSizeMB = isTrial ? MAX_FILE_SIZE_FREE : fileUploadLimit || PRO_MAX_FILE_SIZES[productId] || maxFileSizeMB
    const input = createFileInput(DROPZONE_ACCEPTED_FILES_PRO)

    requestAnimationFrame(() => {
      input.addEventListener('change', (event) => {
        const filesToAdd = Array.from(event.target.files || [])
        if (!filesToAdd.length) return

        const validFiles = []

        for (const fileToAdd of filesToAdd) {
          if (
            [selectedFile, ...addedFiles, fileToAdd].some((file) =>
              shouldProcessAsZip(file.type || file.fileType, file.name)
            ) &&
            [selectedFile, ...addedFiles, ...validFiles, fileToAdd].length > 1
          ) {
            if ([selectedFile, ...addedFiles, fileToAdd].some((file) => file && file.isFolder)) {
              toast.error(MIXED_FOLDER_ERROR_MSG)
            } else {
              toast.error(MIXED_ZIP_ERROR_MSG)
            }
            return
          }

          if (!isFileNameUnique([selectedFile, ...addedFiles, ...validFiles], fileToAdd)) {
            alert('Please upload files with different names')
            return
          }

          if (isTotalSizeWithinLimit(fileToAdd, [selectedFile, ...addedFiles, ...validFiles], maxSizeMB)) {
            validFiles.push(fileToAdd)
          } else if (showUpgradeModal) {
            showUpgradeModal(fileToAdd, maxSizeMB, true)
            return
          } else {
            alert(`Total files should be less than ${maxSizeMB}MB on your current plan.`)
            return
          }
        }

        if (validFiles.length > 0) {
          const updatedFiles = [...addedFiles, ...validFiles]
          setFieldValue('uploadValues.addedFiles', updatedFiles)
          isNewHtmlFilesUploaded([selectedFile, ...updatedFiles])
        }
      })
    })

    input.click()
  }

  const transformFiles = (files) => {
    return files.map((file) => {
      const isFolder = file.isFolder
      const fileName =
        isFolder && file.folderName ? file.folderName : file.path || file.name || file.fileName || 'Unknown file'
      const fileType = file.type || file.fileType || 'application/octet-stream'

      return {
        ...file,
        sourceName: file.name || file.path || file.fileName,
        icon: isFolder ? 'folder.svg' : getFileIcon(fileType, fileName),
        name: fileName,
        size: file.size || 0,
        type: fileType
      }
    })
  }

  const handleFileListDelete = (file) => {
    const sourceName = file.sourceName || file.name || file.path || file.fileName
    const index = allFiles.findIndex((f) => (f.name || f.path || f.fileName) === sourceName)
    handleFileDelete(file, index)
  }

  const handleSetAsIndex = (fileName) => {
    setFieldValue('uploadValues.indexFile', fileName)
  }

  useEffect(() => {
    if (draggedFile) {
      const files = Array.isArray(draggedFile) ? draggedFile : [draggedFile]
      const isReplacement = files.some((f) => f?.isFolder)
      handleFilesSelected(files, isReplacement).then(() => {
        setFieldValue('uploadValues.draggedFile', null)
        resetDraggedFile()
      })
    }
  }, [draggedFile])

  useEffect(() => {
    const checkZipFile = async () => {
      if (selectedFile && shouldProcessAsZip(selectedFile.type || selectedFile.fileType, selectedFile.name)) {
        await zipFileUploaded(selectedFile)
      }
    }
    checkZipFile()
  }, [selectedFile])

  useEffect(() => {
    if (allFiles.length > 0 && !indexFile && !draggedFile) {
      isNewHtmlFilesUploaded(allFiles)
    }
  }, [])

  const shouldShowAddButton =
    selectedFile &&
    !shouldProcessAsZip(selectedFile?.type || selectedFile?.fileType, selectedFile?.name) &&
    !isWasmerFile(selectedFile) &&
    !isGithub &&
    !allFiles.some((f) => f?.isFolder)

  const shouldShowReplaceButton = hasSingleFile && !isGithub
  const isEmptyProject = allFiles.length === 0

  if (isEmptyProject) {
    return (
      <div
        className={styles.emptyStateContainer}
        onClick={(e) => {
          e.preventDefault()
          createFilePicker((files) => {
            handleFilesSelected(files, true)
          }, DROPZONE_ACCEPTED_FILES_PRO)
        }}
        data-testid="SelectedFile-upload-files-empty"
      >
        <div className={styles.emptyStateContent}>
          <div className={styles.placeholderState}>
            <img src="/assets/icons/file-x.svg" alt="No files" height="36" />
            <span>No files uploaded</span>
          </div>
          <div className={styles.hoverState}>
            <span className={styles.hoverText}>
              <img src="/assets/icons/upload.png" className="me-3" alt="replace" height="20" width="20" />
              Upload files
            </span>
          </div>
        </div>
      </div>
    )
  }

  const transformedFiles = transformFiles(allFiles)

  return (
    <>
      <FileListView
        files={transformedFiles}
        onDelete={handleFileListDelete}
        onReplace={() => {
          createFilePicker((files) => {
            handleFilesSelected(files, true)
          }, DROPZONE_ACCEPTED_FILES_PRO)
        }}
        onSetAsIndex={handleSetAsIndex}
        indexFile={indexFile}
        maxVisibleFiles={3}
        className={className}
        isGithub={isGithub}
        hasSingleFile={hasSingleFile}
        isMobile={isMobile}
      />

      {shouldShowReplaceButton && isMobile && (
        <button
          type="button"
          className={`${styles.addFilesBtn} ${styles.replaceFileBtn}`}
          onClick={(e) => {
            e.preventDefault()
            createFilePicker((files) => {
              handleFilesSelected(files, true)
            }, DROPZONE_ACCEPTED_FILES_PRO)
          }}
          aria-label="Replace file"
          data-testid="SelectedFile-replace-file"
        >
          Replace file
        </button>
      )}

      {/* Add more files button */}
      {shouldShowAddButton && (
        <button
          type="button"
          className={styles.addFilesBtn}
          onClick={(e) => {
            e.preventDefault()
            handleAddMoreFiles()
          }}
          aria-label="Add more files"
          data-testid="SelectedFile-add-more-files"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 12H19" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Add more files
        </button>
      )}

      {/* HTML File Picker Modal */}
      {htmlFiles?.length > 0 && (
        <FileListModal
          show={showFileList}
          onHide={() => setShowFileList(false)}
          onFileSelected={(file) => {
            setFieldValue('uploadValues.indexFile', file)
            setShowFileList(false)
          }}
          filesList={htmlFiles}
          indexFile={indexFile}
          title="Which file should be your homepage?"
          subtitle="We've found multiple HTML files in your Zip"
        />
      )}

      {/* PHP File Picker Modal */}
      {phpFiles?.length > 0 && (
        <FileListModal
          show={showFileList}
          onHide={() => setShowFileList(false)}
          onFileSelected={(file) => {
            setFieldValue('uploadValues', {
              ...uploadValues,
              indexFile: file,
              isZipPhpFile: true
            })
            if (domainValues && isWasmerFile(file)) {
              setFieldValue('domainValues', {
                ...domainValues,
                domainSuffix: TLDS.TIINY_IO,
                tlds: [TLDS.TIINY_IO]
              })
            }
            setShowFileList(false)
          }}
          filesList={phpFiles}
          title="Which file should be your homepage?"
        />
      )}
    </>
  )
}

export default SelectedFile
